import { useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FiAlertTriangle } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import Select from 'components/Common/Select';
import Input from 'components/Common/Input';
import type { CancelSubscriptionBillingPreview, CancelSubscriptionPayload } from '../types';
import { CANCELLATION_REASONS } from '@/constants/cancellationReasons';
import { useGetOrganizations } from '@/apis/agency/organizations/hook';

type Props = {
  open: boolean;
  preview: CancelSubscriptionBillingPreview;
  onClose: () => void;
  onConfirm: (payload: CancelSubscriptionPayload) => void;
  isAgencyView: boolean;
};

type CancelReasonFormValues = {
  cancellation_reason: string;
  cancellation_feedback: string;
};

const reasonOptions = CANCELLATION_REASONS.map((reason) => ({
  value: reason.value,
  label: reason.label
}));

export default function CancelSubscriptionConfirmModal({ open, preview, onClose, onConfirm, isAgencyView }: Props) {
  const { data: organizations } = useGetOrganizations({ enabled: !isAgencyView });
  const supportContactName = isAgencyView ? 'enterprise' : organizations?.name || 'agency';

  const { control, handleSubmit, reset, watch } = useForm<CancelReasonFormValues>({
    defaultValues: {
      cancellation_reason: '',
      cancellation_feedback: ''
    },
    mode: 'onChange'
  });

  const cancellationReason = watch('cancellation_reason');
  const cancellationFeedback = watch('cancellation_feedback');

  useEffect(() => {
    if (!open) {
      reset({
        cancellation_reason: '',
        cancellation_feedback: ''
      });
    }
  }, [open, reset]);

  const handleClose = () => {
    onClose();
  };

  const onSubmit = (values: CancelReasonFormValues) => {
    onConfirm({
      cancellation_reason: values.cancellation_reason,
      cancellation_feedback: values.cancellation_feedback.trim()
    });
  };

  const handleConfirm = () => {
    handleSubmit(onSubmit)();
  };

  const isConfirmDisabled = !cancellationReason || !cancellationFeedback?.trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 570,
          maxHeight: 'calc(100vh - 32px)'
        }
      }}
    >
      <IconButton
        aria-label='Close'
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', zIndex: 1 }}
      >
        <IoClose size={22} />
      </IconButton>

      <DialogContent sx={{ pt: 3, pb: 1, px: 2.5 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: '#FDECEC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 1.5
          }}
        >
          <FiAlertTriangle size={32} color='#E53935' />
        </Box>

        <Typography variant='h6' fontWeight={500} textAlign='center' mb={0.5}>
          Cancel Subscription?
        </Typography>
        <Typography variant='body2' color='text.secondary' textAlign='center' mb={2}>
          This action will immediately cancel your subscription. Here&apos;s what will happen
        </Typography>

        <Box bgcolor='#ECECEC' borderRadius='10px' px={1.5} py={1}>
          <Row label='Billing period' value={preview.billingPeriodLabel} />
          <Row label='Days used' value={`${preview.daysUsed} days`} />
          <Row label='Package Total' value={preview.packageTotalFormatted} />
          <Row label='Add-Ons Total' value={preview.addonsTotalFormatted} />
          <Box display='flex' justifyContent='space-between' alignItems='baseline' borderTop='1px solid #ECECEC'>
            <Typography fontWeight={500} fontSize={15}>
              Total
            </Typography>
            <Typography fontSize={15} fontWeight={500} color='#DC2626'>
              {preview.totalFormatted}
            </Typography>
          </Box>
        </Box>

        <Box border='1px solid #ECECEC' borderRadius='10px' px={1.5} py={1} my={1}>
          <Box
            component='ul'
            sx={{
              m: 0,
              pl: 2,
              color: 'text.secondary',
              fontSize: 14,
              '& li': { mb: 0.5, '&:last-of-type': { mb: 0 } }
            }}
          >
            <li>The prorated amount for your current package will be charged for the period used.</li>
            <li>Add-ons will be charged according to their respective commitment periods.</li>
            <li>All saved payment methods will be removed.</li>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: '#FFFBEB',
            borderRadius: '8px',
            p: '8px 12px',
            mb: 1.5
          }}
        >
          <Typography fontWeight={500} color='#D97706' mb={0.75} fontSize={16}>
            Account Inactive — 30-Day Grace Period
          </Typography>
          <Box
            component='ul'
            sx={{ m: 0, pl: 2.5, color: '#D97706', fontSize: 14, '& li': { mb: 0.25, '&:last-of-type': { mb: 0 } } }}
          >
            {isAgencyView ? (
              <>
                <li>
                  Your account will move to{' '}
                  <Box component='span' fontWeight={500}>
                    Inactive status
                  </Box>
                  .
                </li>
                <li>
                  To restore services, contact{' '}
                  <Box component='span' fontWeight={500}>
                    {supportContactName}
                  </Box>{' '}
                  support.
                </li>
                <li>
                  No customer activity will be processed for your{' '}
                  <Box component='span' fontWeight={500}>
                    agency
                  </Box>{' '}
                  or its{' '}
                  <Box component='span' fontWeight={500}>
                    associated businesses
                  </Box>{' '}
                  while your account is inactive.
                </li>
                <li>
                  After 30 days, your{' '}
                  <Box component='span' fontWeight={500}>
                    agency and all associated businesses
                  </Box>{' '}
                  will move to{' '}
                  <Box component='span' fontWeight={500}>
                    Archived state
                  </Box>{' '}
                  and all associated data will be permanently deleted and cannot be recovered.
                </li>
              </>
            ) : (
              <>
                <li>
                  Your account will move to{' '}
                  <Box component='span' fontWeight={500}>
                    Inactive status
                  </Box>
                </li>
                <li>
                  After 30 days, your business will move to{' '}
                  <Box component='span' fontWeight={500}>
                    Archived
                  </Box>{' '}
                  state and all associated data will be permanently deleted and cannot be recovered.
                </li>
                <li>To restore services, contact {supportContactName} support.</li>
                <li>No customer activity will be processed while the business is inactive.</li>
              </>
            )}
          </Box>
        </Box>

        <Box display='flex' flexDirection='column' gap={1.5}>
          <Select
            name='cancellation_reason'
            control={control}
            label='Reason'
            required
            options={reasonOptions}
            sortOrder='none'
            rules={{ required: 'Reason is required' }}
          />
          <Input
            name='cancellation_feedback'
            control={control}
            label='Feedback'
            required
            fullWidth
            multiline
            minRows={2}
            placeholder='Tell us more about your experience'
            rules={{ required: 'Feedback is required' }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1, justifyContent: 'flex-end' }}>
        <Button variant='outlined' color='inherit' onClick={handleClose} sx={{ borderColor: '#D1D5DB' }}>
          Keep Subscription
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          sx={{
            bgcolor: '#DC2626',
            '&:hover': { bgcolor: '#B91C1C' }
          }}
        >
          Confirm & Pay {preview.totalFormatted}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Box display='flex' justifyContent='space-between' gap={2} mb={0.75}>
      <Typography fontSize={14} color='text.secondary'>
        {label}
      </Typography>
      <Typography fontSize={15} fontWeight={500} textAlign='right'>
        {value}
      </Typography>
    </Box>
  );
}
